import { ErrorHandler, inject, Injectable, Injector, NgZone } from '@angular/core';
import { LoggerService } from '@shared/modules/logs/services/logger.service';
import { LogLevel } from '@shared/modules/logs/common/constants/log-level';
import { TrackingService } from '@shared/modules/tracking/tracking.service';
import { TrackingConstants } from '@shared/modules/tracking/tracking.constants';

const TRACK_DEDUP_WINDOW_MS = 5 * 60 * 1000;
const TRACK_CAP_WINDOW_MS = 60 * 60 * 1000;
const MAX_TRACKED_ERRORS_PER_WINDOW = 50;
const MAX_STACK_LENGTH = 2000;

@Injectable()
export class CustomErrorHandler implements ErrorHandler {
  private readonly loggerService: LoggerService = inject(LoggerService);
  // Resolved lazily to avoid a construction-time DI cycle: ErrorHandler is created very
  // early, while TrackingService pulls in the auth/session graph.
  private readonly injector: Injector = inject(Injector);
  private readonly ngZone: NgZone = inject(NgZone);

  // fingerprint -> last-tracked timestamp, used to dedup recurring errors.
  private readonly lastTrackedAtByFingerprint: Map<string, number> = new Map<string, number>();
  private trackedInWindowCount = 0;
  private windowStartedAt = 0;

  handleError(error: any) {
    try {
      this.loggerService.error({
        error: error,
        log: {
          message: 'Handling error: ',
          level: LogLevel.ERROR
        }
      });
      this.trackError(error);
      console.error(error);
    } catch (err) {
      console.error(err);
    }
  }

  private trackError(error: any): void {
    try {
      const fingerprint = `${error?.name}: ${error?.message ?? String(error)}`;
      const now = Date.now();

      // Bound the map's lifetime memory footprint for long-running sessions (Electron
      // can stay open for weeks) by periodically evicting stale fingerprints.
      if (this.lastTrackedAtByFingerprint.size > 200) {
        for (const [key, lastSeenAt] of this.lastTrackedAtByFingerprint) {
          if (now - lastSeenAt >= TRACK_DEDUP_WINDOW_MS) {
            this.lastTrackedAtByFingerprint.delete(key);
          }
        }
      }

      const lastTrackedAt = this.lastTrackedAtByFingerprint.get(fingerprint);

      if (lastTrackedAt !== undefined && now - lastTrackedAt < TRACK_DEDUP_WINDOW_MS) {
        return;
      }

      // Rolling cap instead of a session-lifetime one, so tracking resumes each hour
      // rather than going permanently silent in long-running sessions.
      if (now - this.windowStartedAt >= TRACK_CAP_WINDOW_MS) {
        this.windowStartedAt = now;
        this.trackedInWindowCount = 0;
      }
      if (this.trackedInWindowCount >= MAX_TRACKED_ERRORS_PER_WINDOW) {
        return;
      }

      this.lastTrackedAtByFingerprint.set(fingerprint, now);
      this.trackedInWindowCount++;

      // Tracking must not itself trigger change detection, or a recurring error could
      // feed back into another error via CD.
      this.ngZone.runOutsideAngular(() => {
        this.injector.get(TrackingService).track(TrackingConstants.error, {
          Message: error?.message ?? String(error),
          Name: error?.name,
          Stack: error?.stack?.slice(0, MAX_STACK_LENGTH)
        });
      });
    } catch (err) {
      console.error(err);
    }
  }
}
